import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import { getLastMonths, formatMonth } from "../utils/dateUtils";
import { formatCurrency } from "../utils/currencyUtils";
import { useAuth } from "../contexts/AuthContext";

// ─── Email Quota ──────────────────────────────────────────────────────────────

const EmailQuotaWidget = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["emailQuota"],
    queryFn: () => apiService.getEmailQuota(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const pct = data ? Math.min(100, Math.round((data.totalSentToday / data.dailyLimit) * 100)) : 0;
  const barColor = pct >= 90 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#16a34a";

  return (
    <div style={{ marginTop: "2.5rem" }}>
      <span style={{
        fontSize: "0.85rem", fontWeight: "600", color: "#001272",
        opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.05em",
        display: "block", marginBottom: "0.75rem",
      }}>
        Email Quota Today
      </span>

      {isLoading ? (
        <p style={{ fontSize: "0.8rem", color: "#9ca3af" }}>Loading…</p>
      ) : data ? (
        <div style={{
          background: "white", borderRadius: "0.375rem",
          border: "1px solid #f1f5f9", padding: "0.75rem",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.4rem" }}>
            <span style={{ fontSize: "1.1rem", fontWeight: "700", color: "#001272" }}>
              {data.totalSentToday}
              <span style={{ fontSize: "0.75rem", fontWeight: "400", color: "#64748b" }}>
                {" "}/ {data.dailyLimit}
              </span>
            </span>
            <span style={{ fontSize: "0.75rem", color: barColor, fontWeight: "600" }}>
              {pct}%
            </span>
          </div>

          <div style={{ height: 6, borderRadius: 3, background: "#f1f5f9", overflow: "hidden", marginBottom: "0.6rem" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: barColor, borderRadius: 3, transition: "width 0.4s" }} />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#64748b" }}>
              <span>Login links</span>
              <span>{data.loginEmailsSentToday}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#64748b" }}>
              <span>Payroll emails</span>
              <span>{data.payrollEmailsSentToday}</span>
            </div>
            {data.pendingInQueue > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#f59e0b", marginTop: "0.2rem", paddingTop: "0.2rem", borderTop: "1px solid #f1f5f9" }}>
                <span>Pending in queue</span>
                <span style={{ fontWeight: "600" }}>{data.pendingInQueue}</span>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ─── Recent Syncs ─────────────────────────────────────────────────────────────

const JOB_TYPE_LABEL = {
  TUTOR_SYNC: "Tutor Sync",
  STUDENT_SYNC: "Student Sync",
  APPOINTMENT_SYNC: "Appointment Sync",
  NOTE_SYNC: "Note Sync",
  REPORT_SYNC: "Report Sync",
  SCHEDULED_SYNC: "Full Sync",
  DEMO_LESSON_SYNC: "Demo Sync",
  PAYROLL_VALIDATION: "Payroll Validation",
  PAYROLL_GENERATION: "Payroll Generation",
};

const STATUS_DOT = {
  IN_PROGRESS: { color: "#f59e0b", label: "In Progress", pulse: true },
  PENDING: { color: "#f59e0b", label: "Pending", pulse: true },
  COMPLETED: { color: "#16a34a", label: "Completed", pulse: false },
  FAILED: { color: "#dc2626", label: "Failed", pulse: false },
  CANCELLED: { color: "#9ca3af", label: "Cancelled", pulse: false },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

const RecentSyncs = () => {
  const { data: jobs = [] } = useQuery({
    queryKey: ["recentSyncJobs"],
    queryFn: () => apiService.getRecentJobs(10),
    refetchInterval: 15000,
    staleTime: 10000,
  });

  const hasActive = jobs.some(
    (j) => j.status === "IN_PROGRESS" || j.status === "PENDING",
  );

  return (
    <div style={{ marginTop: "2.5rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          marginBottom: "0.75rem",
        }}
      >
        <span
          style={{
            fontSize: "0.85rem",
            fontWeight: "600",
            color: "#001272",
            opacity: 0.7,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Recent Activity
        </span>
        {hasActive && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.3rem",
              fontSize: "0.75rem",
              color: "#f59e0b",
              fontWeight: "500",
            }}
          >
            <span
              className="sync-pulse"
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#f59e0b",
                display: "inline-block",
              }}
            />
            sync in progress
          </span>
        )}
      </div>
      {jobs.length === 0 ? (
        <p style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
          No sync jobs recorded yet.
        </p>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}
        >
          {jobs.map((job) => {
            const dot = STATUS_DOT[job.status] || STATUS_DOT.CANCELLED;
            return (
              <div
                key={job.jobId}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.15rem",
                  padding: "0.4rem 0.75rem",
                  background: "white",
                  borderRadius: "0.375rem",
                  border: "1px solid #f1f5f9",
                  fontSize: "0.8rem",
                  color: "#374151",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                  <span
                    className={dot.pulse ? "sync-pulse" : ""}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: dot.color,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontWeight: "500", minWidth: "7rem" }}>
                    {JOB_TYPE_LABEL[job.jobType] || job.jobType}
                  </span>
                  {job.yearMonth && (
                    <span
                      style={{
                        color: "#6b7280",
                        fontSize: "0.75rem",
                        flexShrink: 0,
                      }}
                    >
                      {job.yearMonth}
                    </span>
                  )}
                  <span style={{ color: "#6b7280", flexGrow: 1 }}>
                    {dot.label}
                    {job.progressMessage && job.status === "IN_PROGRESS"
                      ? ` — ${job.progressMessage}`
                      : ""}
                  </span>
                  <span style={{ color: "#9ca3af", flexShrink: 0 }}>
                    {timeAgo(job.createdAt)}
                  </span>
                  {job.formattedDuration && job.formattedDuration !== "N/A" && (
                    <span style={{ color: "#9ca3af", flexShrink: 0 }}>
                      ({job.formattedDuration})
                    </span>
                  )}
                </div>
                {job.resultSummary && job.status !== "IN_PROGRESS" && (
                  <div style={{ color: "#6b7280", fontSize: "0.75rem", paddingLeft: "1.4rem" }}>
                    {job.resultSummary}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const STATUS_BADGE = {
  LOCKED: { bg: "#dcfce7", color: "#166534", label: "Locked" },
  DRAFT: { bg: "#fef9c3", color: "#854d0e", label: "Draft" },
  NONE: { bg: "#fee2e2", color: "#991b1b", label: "Not Generated" },
  PRE_GENERATION: { bg: "#dbeafe", color: "#1e40af", label: "Pre-Generation" },
};

const CURRENT_MONTH = new Date().toISOString().slice(0, 7);

// MonthCard component
const MonthCard = ({ month, onViewDetails, styles, isSuperAdmin }) => {
  const { data: payrollRun, isLoading } = useQuery({
    queryKey: ["payrollRun", month],
    queryFn: () => apiService.getPayrollRun(month),
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });

  const isCurrentMonth = month === CURRENT_MONTH;

  const badge = payrollRun
    ? STATUS_BADGE[payrollRun.status] || STATUS_BADGE.DRAFT
    : isCurrentMonth
      ? STATUS_BADGE.PRE_GENERATION
      : STATUS_BADGE.NONE;

  // Financial info is only shown to super admins
  const payoutDisplay = isSuperAdmin
    ? (isLoading ? "Loading..." : payrollRun?.totalNet != null ? formatCurrency(payrollRun.totalNet) : "—")
    : null;

  const subDisplay = isSuperAdmin
    ? (isLoading
        ? ""
        : payrollRun
          ? `Gross ${formatCurrency(payrollRun.totalGross)} · Ded. ${formatCurrency(payrollRun.totalDeductions)}`
          : isCurrentMonth
            ? "Add adjustments or run syncs"
            : "No payroll generated")
    : (payrollRun ? null : isCurrentMonth ? "Add adjustments or run syncs" : "No payroll generated");

  return (
    <div
      style={styles.monthCard}
      onClick={() => onViewDetails(month)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = styles.monthCardHover.transform;
        e.currentTarget.style.boxShadow = styles.monthCardHover.boxShadow;
        e.currentTarget.style.cursor = "pointer";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = styles.monthCard.boxShadow;
        e.currentTarget.style.cursor = "default";
      }}
    >
      <div style={styles.monthHeader}>
        <h3 style={styles.monthHeaderH3}>{formatMonth(month)}</h3>
        <div
          style={{
            ...styles.monthStatus,
            backgroundColor: badge.bg,
            color: badge.color,
          }}
        >
          {badge.label}
        </div>
      </div>

      {isSuperAdmin ? (
        <div style={styles.monthStats}>
          <div style={styles.stat}>
            <span style={styles.statLabel}>Net Payout:</span>
            <span
              style={{
                ...styles.statValue,
                ...(isLoading ? { color: "#64748b", fontStyle: "italic" } : {}),
              }}
            >
              {payoutDisplay}
            </span>
          </div>
          <div style={{ ...styles.stat, ...styles.statLast }}>
            <span style={{ fontSize: "0.75rem", color: "#64748b" }}>
              {subDisplay}
            </span>
          </div>
        </div>
      ) : subDisplay ? (
        <div style={styles.monthStats}>
          <div style={{ ...styles.stat, ...styles.statLast }}>
            <span style={{ fontSize: "0.75rem", color: "#64748b" }}>{subDisplay}</span>
          </div>
        </div>
      ) : null}

      <div style={styles.monthActions}>
        <div
          style={{
            fontSize: "0.875rem",
            color: "#001272",
            opacity: "0.8",
            textAlign: "center",
            paddingTop: "0.5rem",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          Click to view details
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const [months] = useState(() =>
    getLastMonths(13)
      .filter((m) => m >= "2026-01"),
  );

  const handleViewDetails = (month) => {
    navigate(`/month/${month}`);
  };

  // Styles
  const styles = {
    dashboardPage: {
      padding: "1rem 0",
    },
    dashboardHeader: {
      marginBottom: "2rem",
    },
    dashboardHeaderH1: {
      fontSize: "1.875rem",
      fontWeight: "700",
      color: "#001272",
      marginBottom: "0.5rem",
    },
    dashboardHeaderP: {
      color: "#001272",
      opacity: "0.8",
      fontSize: "1rem",
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "4rem 0",
    },
    spinner: {
      border: "3px solid #f3f3f3",
      borderTop: "3px solid #53bdeb",
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      animation: "spin 1s linear infinite",
      marginBottom: "1rem",
    },
    monthsGrid: {
      display: "grid",
      gap: "1.5rem",
    },
    monthCard: {
      background: "white",
      borderRadius: "0.5rem",
      boxShadow:
        "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
      padding: "1.5rem",
      transition: "transform 0.2s, box-shadow 0.2s",
      border: "1px solid #f1f5f9",
      cursor: "default",
    },
    monthCardHover: {
      transform: "translateY(-2px)",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    },
    monthHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1.5rem",
    },
    monthHeaderH3: {
      fontSize: "1.25rem",
      fontWeight: "600",
      color: "#001272",
      margin: "0",
    },
    monthStatus: {
      fontSize: "0.875rem",
      fontWeight: "500",
      padding: "0.25rem 0.75rem",
      borderRadius: "9999px",
    },
    monthStatusGenerated: {
      backgroundColor: "#dcfce7",
      color: "#166534",
    },
    monthStatusNotGenerated: {
      backgroundColor: "#fee2e2",
      color: "#991b1b",
    },
    monthStats: {
      marginBottom: "1.5rem",
    },
    stat: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "0.5rem 0",
      borderBottom: "1px solid #f1f5f9",
    },
    statLast: {
      borderBottom: "none",
    },
    statLabel: {
      color: "#001272",
      opacity: "0.8",
      fontSize: "0.875rem",
    },
    statValue: {
      fontWeight: "600",
      color: "#001272",
    },

    monthActions: {
      display: "flex",
      gap: "0.75rem",
    },
  };

  return (
    <div style={styles.dashboardPage}>
      <div style={styles.dashboardHeader}>
        <h1 style={styles.dashboardHeaderH1}>Payroll Dashboard</h1>
        <p style={styles.dashboardHeaderP}>
          Overview of payroll data from January 2026 onward
        </p>
      </div>

      <div className="dashboard-main-layout" style={{ display: "flex", gap: "2rem", alignItems: "flex-start" }}>
        <div style={{ flex: "2 1 0", minWidth: 0 }}>
          <div className="months-grid" style={styles.monthsGrid}>
            {months.map((month) => (
              <MonthCard
                key={month}
                month={month}
                onViewDetails={handleViewDetails}
                styles={styles}
                isSuperAdmin={isSuperAdmin}
              />
            ))}
          </div>
        </div>
        <div style={{ flex: "1 1 0", minWidth: 0 }}>
          <RecentSyncs />
          <EmailQuotaWidget />
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
          .sync-pulse { animation: pulse 1.5s ease-in-out infinite; }

          .months-grid {
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          }

          .spinner {
            display: inline-block;
            border: 2px solid #f3f3f3;
            border-top: 2px solid #53bdeb;
            border-radius: 50%;
            width: 16px;
            height: 16px;
            animation: spin 1s linear infinite;
            vertical-align: middle;
          }

          @media (max-width: 768px) {
            .dashboard-main-layout {
              flex-direction: column;
            }

            .dashboard-main-layout > * {
              flex: none !important;
              width: 100%;
            }

            .months-grid {
              grid-template-columns: 1fr;
            }

            .month-card {
              padding: 1.25rem;
            }

            .month-actions {
              flex-direction: column;
            }
          }

          @media (max-width: 640px) {
            .dashboard-header h1 {
              font-size: 1.5rem;
            }

            .dashboard-header p {
              font-size: 0.875rem;
            }
          }
        `}
      </style>
    </div>
  );
};

export default DashboardPage;
